import React, { useMemo, useState } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
    Box, Button, CircularProgress, IconButton, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip,
} from '@mui/material';
import { Booking, BookingTableEntry } from './types';
import StatusBadge, { BookingStatusType } from '../../../components/StatusBadge';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { useTranslation } from 'react-i18next';
import { PaymentStatusType } from '../../../api/booking/bookingApiSlice';
import { formatDate, formatDateTime } from '../../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { makeHasPermissionSelector, tenantSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import {
    COVERED_PAYMENT_STATUS,
    isCoveredByOrganization,
} from '../../../utils/bookingCoverage';
import { finishTimingLabelKey, getFinishTiming } from '../../../utils/bookingFinishTiming';
import { approvalLabelKey, toApprovalState } from '../../../utils/bookingApproval';
import BookingArchiveDialog, { BookingArchiveMode } from './BookingArchiveDialog';
import { tenantHasLockerColumn } from '../../../utils/lockerSearch';
import { isRoomFamily, spaceOf } from '../../../utils/useCases';
import Checkbox from '../../../components/ui/Checkbox';
import { headerSelectionState } from '../../../utils/bookingBulkActions';
import { onRowClick } from '../../../utils/rowClick';

interface BookingTableProps {
    entries: BookingTableEntry[];
    loading?: boolean;
    onViewDetails: (bookingId: string, bookingNumber: string) => void;
    onViewSeriesDetails: (seriesId: string) => void;
    onRefetch?: () => void;
    /** Show the locker column even where the tenant's use cases hide it —
     *  set while the list is filtered by locker, so the answer is visible. */
    forceLockerColumn?: boolean;
    /**
     * Bulk actions (#3016): the selected booking ids. With it the table shows
     * a checkbox per booking row, a series occurrence included (each is its
     * own booking), and a select-all box for the rows on the current page.
     */
    selectedIds?: ReadonlySet<string>;
    onToggleSelected?: (bookingId: string) => void;
    /** The select-all box, with the booking ids it covers. */
    onToggleAllSelected?: (bookingIds: string[]) => void;
}

const BookingTable: React.FC<BookingTableProps> = ({
    entries,
    loading = false,
    onViewDetails,
    onViewSeriesDetails,
    onRefetch,
    forceLockerColumn = false,
    selectedIds,
    onToggleSelected,
    onToggleAllSelected,
}) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const config = useSelector((state: RootState) => state.authentication.config);
    const useCases = config?.services;
    const selectedTenant = useSelector(tenantSelector);
    // Tenant-wide, as before the use-case sweep: a tenant running a use case
    // booked by the night (apartment) gets date-only times, check-in/out
    // headers and coverage badges on EVERY row, venues included (known defect,
    // docs/reviews/use-case-name-sites-inventory.md "Defects found in passing").
    const isRoomRental = (useCases ?? []).some((useCase) => spaceOf(useCase)?.timeUnit === 'night')
        && selectedTenant !== 'demo';
    const hasArchivePermission = useSelector(makeHasPermissionSelector(Permission.archiveBookings()));
    const hasAnonymizePermission = useSelector(makeHasPermissionSelector(Permission.anonymizeBookings()));

    const [actionBooking, setActionBooking] = useState<Booking | null>(null);
    const [actionMode, setActionMode] = useState<BookingArchiveMode | null>(null);

    const openAction = (booking: Booking, mode: BookingArchiveMode) => {
        setActionBooking(booking);
        setActionMode(mode);
    };

    const closeAction = () => {
        setActionMode(null);
        setActionBooking(null);
    };

    // Row actions (design-decisions.md › Zeilenaktionen in Tabellen): a click
    // on the row opens the booking — the booking number is the keyboard's way
    // in — and the changing actions sit in the row's ⋮ menu, Anonymisieren
    // (irreversible) last behind the divider. An anonymised booking, or a role
    // without the archive rights, has nothing to change and gets no ⋮.
    const renderRowActions = (booking: Booking) => {
        const isArchived = !!booking.archivedAt;
        const isAnonymized = !!booking.anonymizedAt;
        return (
            <RowActionsMenu
                rowName={booking.bookingNumber}
                items={[
                    {
                        id: 'archive',
                        label: t('bookings.archive.button'),
                        onClick: () => openAction(booking, 'archive'),
                        hidden: !hasArchivePermission || isArchived || isAnonymized,
                    },
                    {
                        id: 'restore',
                        label: t('bookings.archive.restore'),
                        onClick: () => openAction(booking, 'restore'),
                        hidden: !hasArchivePermission || !isArchived || isAnonymized,
                    },
                ]}
                destructiveItems={[{
                    id: 'anonymize',
                    label: t('bookings.archive.anonymize'),
                    onClick: () => openAction(booking, 'anonymize'),
                    hidden: !hasAnonymizePermission || !isArchived || isAnonymized,
                }]}
            />
        );
    };
    const getPaymentStatusLabel = (status: string) => {
        const normalizedStatus = status?.toLowerCase();
        if (normalizedStatus === 'success' || normalizedStatus === 'paid') return 'paid';
        if (normalizedStatus === 'open') return 'unpaid';
        return status;
    };
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const saved = localStorage.getItem('bookings_rows_per_page');
        const defaultVal = 100;
        return saved && [100, 200, 300, 500].includes(Number(saved)) ? Number(saved) : defaultVal;
    });

    const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set());

    const toggleSeries = (seriesId: string) => {
        setExpandedSeries(prev => {
            const next = new Set(prev);
            if (next.has(seriesId)) {
                next.delete(seriesId);
            } else {
                next.add(seriesId);
            }
            return next;
        });
    };

    const getIssueReportLabel = (issues: any[]) => {
        if (issues.length > 0) {
            return 'yes';
        }
        return 'no';
    };

    // "In time" / "Overtime" annotation next to the status badge of a finished
    // booking. Uses the same shared helper and the same labels as the
    // all-tenant list — the two surfaces described the same booking
    // differently before, which is confusing during a check-in.
    const renderFinishTiming = (booking: Booking) => {
        const timing = getFinishTiming(
            booking?.bookingStatus,
            booking?.finishedAtDate,
            booking?.endDate,
        );
        if (!timing) return null;
        return (
            <span className={'text-xs text-foreground/50'}>
                {t(finishTimingLabelKey(timing))}
            </span>
        );
    };

    // M8 approval (#1809): "noch nicht freigegeben" / "freigegeben" next to a
    // reserved room booking, "abgelehnt" next to a declined one. Same muted
    // sub-label as the finish timing above, so the badge itself stays the
    // one status the booking has.
    const renderApproval = (booking: Booking) => {
        const state = toApprovalState(booking?.approvalState);
        if (!state) return null;
        return (
            <span className={'text-xs text-foreground/50'}>
                {t(approvalLabelKey(state))}
            </span>
        );
    };

    const statusColors: Record<string, { bg: string, text: string, dot: string }> = {
        active: { bg: '#CFD8FE', text: '#3F66FB', dot: '#3F66FB' },
        booked: { bg: '#CEEFCE', text: '#2D912D', dot: '#2D912D' },
        canceled: { bg: '#F8DFE5', text: '#E94F54', dot: '#E94F54' },
        finished: { bg: '#DEDFDF', text: '#515557', dot: '#515557' },
        reserved: { bg: '#EDE9FE', text: '#7C3AED', dot: '#7C3AED' },
        incompleted: { bg: '#FEF3C7', text: '#D97706', dot: '#D97706' },
        blocked: { bg: '#FEE2E2', text: '#991B1B', dot: '#991B1B' },
        conflicted: { bg: '#FCE7F3', text: '#9D174D', dot: '#9D174D' },
    };

    const paymentColors: Record<string, { bg: string, text: string, dot: string }> = {
        paid: { bg: '#CEEFCE', text: '#2D912D', dot: '#2D912D' },
        unpaid: { bg: '#FCEEC6', text: '#BE6A2B', dot: '#BE6A2B' },
        partially_paid: { bg: '#FCEEC6', text: '#BE6A2B', dot: '#BE6A2B' },
        overpaid: { bg: '#F8DFE5', text: '#E94F54', dot: '#E94F54' },
        // Neutral by design: nothing is owed, so this is a statement of fact
        // rather than a payment state needing attention.
        [COVERED_PAYMENT_STATUS]: { bg: '#EEEEEE', text: '#515557', dot: '#515557' },
    };

    const getStatusSummary = (seriesBookings: Booking[]) => {
        const counts: Record<string, number> = {};
        for (const b of seriesBookings) {
            const status = b.bookingStatus.toLowerCase();
            counts[status] = (counts[status] ?? 0) + 1;
        }
        return Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .map(([status, count]) => ({ status, count }));
    };

    const getPaymentSummary = (seriesBookings: Booking[]) => {
        const counts: Record<string, number> = {};
        for (const b of seriesBookings) {
            // Covered bookings are unpaid on purpose; counting them as unpaid
            // would tell staff a series owes money it never will.
            const status = isCoveredByOrganization(b)
                ? COVERED_PAYMENT_STATUS
                : getPaymentStatusLabel(b.paymentStatus);
            counts[status] = (counts[status] ?? 0) + 1;
        }
        return Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .map(([status, count]) => ({ status, count }));
    };

    // The location/object shown for a booking: room name for the room family,
    // address otherwise. Used to detect when a series spans differing locations.
    const getLocationLabel = (booking: Booking): string =>
        isRoomFamily(booking?.useCase)
            ? booking?.langNames?.[i18n.language === 'de' ? 'de' : 'en']?.title
                ?? booking?.assetName
                ?? ''
            : booking?.address ?? '';

    const [sortKey, setSortKey] = useState({
        key: '',
        order: '',
    });

    const compareBookings = (a: Booking, b: Booking) => {
        if (!sortKey.key) return 0;
        let compareResult = 0;
        if (sortKey.key === 'bookingId') {
            compareResult = a.bookingNumber.localeCompare(b.bookingNumber);
        } else if (sortKey.key === 'checkIn') {
            compareResult = new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        } else if (sortKey.key === 'checkOut') {
            compareResult = new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
        } else if (sortKey.key === 'bookingStatus') {
            compareResult = a.bookingStatus.localeCompare(b.bookingStatus);
        } else if (sortKey.key === 'issue') {
            compareResult = getIssueReportLabel(a.damageReports).localeCompare(getIssueReportLabel(b.damageReports));
        }
        return sortKey.order === 'asc' ? compareResult : -compareResult;
    };

    // Get the "representative" booking for a series (first by current sort) to position the group
    const getRepresentativeBooking = (entry: BookingTableEntry): Booking =>
        entry.type === 'single' ? entry.booking : entry.bookings[0];

    const sortedEntries = useMemo(() => {
        if (!sortKey.key) return entries;

        return [...entries].map(entry => {
            // Sort bookings within each series group
            if (entry.type === 'series') {
                return { ...entry, bookings: [...entry.bookings].sort(compareBookings) };
            }
            return entry;
        }).sort((a, b) => compareBookings(getRepresentativeBooking(a), getRepresentativeBooking(b)));
    }, [entries, sortKey]);

    // Mapping for useCase to user-friendly labels
    const useCaseLabels: Record<string, { en: string, de: string }> = {
        apartment: { en: 'Guest apartments', de: 'Gästewohnungen' },
        venue: { en: 'Venues', de: 'Veranstaltungsräume' },
        hall: { en: 'Sports halls', de: 'Sporthallen' },
        rental: { en: 'Product rental', de: 'Produktmiete' },
        p2p: { en: 'Handover', de: 'Übergabe' },
        storage: { en: 'Storage', de: 'Aufbewahrung' },
        parcel: { en: 'Parcel delivery', de: 'Paketlieferung' },
    };

    const paginatedEntries = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedEntries.slice(start, start + rowsPerPage);
    }, [sortedEntries, page, rowsPerPage]);

    const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);
    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRows = parseInt(event.target.value, 10);
        setRowsPerPage(newRows);
        localStorage.setItem('bookings_rows_per_page', String(newRows));
        setPage(0);
    };

    const showLockerColumn =
        forceLockerColumn || tenantHasLockerColumn(useCases);
    const selectable = !!selectedIds && !!onToggleSelected;
    const columnCount = 8 + (showLockerColumn ? 1 : 0) + (selectable ? 1 : 0);

    // The rows a select-all covers: every booking row on THIS page — a single
    // booking, and each occurrence of a series that is expanded. A collapsed
    // series shows no occurrence, so none of it is ticked unseen.
    const visibleSelectableIds = useMemo(() => paginatedEntries.flatMap((entry) => {
        if (entry.type === 'single') return [entry.booking.bookingId];
        return expandedSeries.has(entry.seriesId) ? entry.bookings.map((b) => b.bookingId) : [];
    }), [paginatedEntries, expandedSeries]);
    const headerState = selectedIds ? headerSelectionState(visibleSelectableIds, selectedIds) : 'none';

    const renderSelectCell = (booking: Booking) => selectedIds && onToggleSelected && (
        // A click on the box selects; it must not also open the booking.
        <TableCell padding={'checkbox'} onClick={(event) => event.stopPropagation()}>
            <Checkbox
                checked={selectedIds.has(booking.bookingId)}
                onChange={() => onToggleSelected(booking.bookingId)}
                ariaLabel={t('bookings.bulk.select_row', { number: booking.bookingNumber })}
            />
        </TableCell>
    );

    /**
     * The payment cell, ONE rendering for the single row and the series sub-row.
     * The two used to be separate copies, and only the sub-row knew about
     * coverage — so every normal covered booking read "Unbezahlt" (#2980).
     *
     * A covered booking (#1926) is unpaid on purpose — the organization's space
     * agreement settles it, nothing is ever payable on it. Showing the plain
     * "unpaid" badge sends staff chasing money that was never owed. Use cases
     * outside the room family show their issue-report state here instead.
     */
    const renderCoverage = (booking: Booking) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <StatusBadge type={'covered'} showDropdown={false} isEditing={false} />
            {booking.coveringOrganizationName && (
                <Box component={'span'} sx={{ opacity: 0.6 }}>
                    {booking.coveringOrganizationName}
                </Box>
            )}
        </Box>
    );

    /**
     * Where the column is the PAYMENT column (tenants with guest apartments),
     * coverage replaces the payment badge. Where it is the PROBLEM column
     * (venue-only tenants), it must not: that column shows issue reports, and a
     * covered booking can have one. Coverage then sits under the booking status
     * instead (`renderCoverageUnderStatus`) — PO, 2026-10-12.
     */
    const renderPaymentCell = (booking: Booking) => {
        if (isRoomRental && isCoveredByOrganization(booking)) return renderCoverage(booking);
        const badgeType = isRoomRental
            ? getPaymentStatusLabel(booking.paymentStatus) as PaymentStatusType
            : getIssueReportLabel(booking.damageReports);
        return <StatusBadge type={badgeType} showDropdown={false} isEditing={false} />;
    };

    /** Coverage under the booking status, where the next column is "Problem". */
    const renderCoverageUnderStatus = (booking: Booking) =>
        !isRoomRental && isCoveredByOrganization(booking) ? renderCoverage(booking) : null;

    /**
     * The booking number: a button that opens the booking — the keyboard's way
     * in, since the row itself opens it on a click. Shared by single rows and
     * series occurrences.
     */
    const renderBookingNumber = (booking: Booking) => (
        <button
            type={'button'}
            className={'text-primary hover:underline'}
            aria-label={t('bookings.bookings_table.open_booking', { number: booking.bookingNumber })}
            onClick={(event) => {
                event.stopPropagation();
                onViewDetails(booking.bookingId, booking.bookingNumber);
            }}
        >
            {booking?.bookingNumber}
        </button>
    );

    const renderBookingRow = (booking: Booking) => (
        <TableRow
            key={booking.bookingId}
            hover={true}
            onClick={onRowClick(() => onViewDetails(booking.bookingId, booking.bookingNumber))}
            sx={{
                cursor: 'pointer',
                ...(booking.archivedAt ? { opacity: 0.6 } : {}),
            }}
        >
            {renderSelectCell(booking)}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 28, flexShrink: 0 }} />
                    {renderBookingNumber(booking)}
                </Box>
            </TableCell>
            <TableCell>
                {useCaseLabels[booking?.useCase as keyof typeof useCaseLabels]?.[currentLanguage as 'en' | 'de'] || booking?.useCase}
            </TableCell>
            <TableCell>
                {booking?.startDate && (currentLanguage === 'de' || currentLanguage === 'en')
                    ? isRoomRental
                        ? formatDate(booking.startDate, currentLanguage)
                        : formatDateTime(booking.startDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                    : ''}
            </TableCell>
            <TableCell>
                {booking?.endDate && (currentLanguage === 'de' || currentLanguage === 'en')
                    ? isRoomRental
                        ? formatDate(booking.endDate, currentLanguage)
                        : formatDateTime(booking.endDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                    : ''}
            </TableCell>
            <TableCell>
                {isRoomFamily(booking?.useCase)
                    ? booking?.langNames?.[i18n.language === 'de' ? 'de' : 'en']?.title ??
                      booking?.assetName ??
                      t('bookings.bookings_table.none')
                    : booking?.address ?? t('bookings.bookings_table.none')}
            </TableCell>

            <TableCell>{booking?.senderFullname}</TableCell>
            {showLockerColumn && (
                <TableCell>{booking?.door ?? t('bookings.bookings_table.none')}</TableCell>
            )}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <StatusBadge
                        type={(booking?.bookingStatus.toLowerCase() as BookingStatusType) ?? 'booked'}
                        isEditing={false}
                        options={[
                            {
                                label: t('booking_details.status.active'),
                                value: 'active',
                            },
                            {
                                label: t('booking_details.status.booked'),
                                value: 'booked',
                            },
                            {
                                label: t('booking_details.status.canceled'),
                                value: 'canceled',
                            },
                            {
                                label: t('booking_details.status.finished'),
                                value: 'finished',
                            },
                        ]}
                    />
                    {booking?.archivedAt && <StatusBadge type={'archived'} isEditing={false} />}
                    {renderFinishTiming(booking)}
                    {renderApproval(booking)}
                    {renderCoverageUnderStatus(booking)}
                </Box>
            </TableCell>
            <TableCell>
                {renderPaymentCell(booking)}
            </TableCell>
            <TableCell>
                {renderRowActions(booking)}
            </TableCell>
        </TableRow>
    );

    const getBookingNumberRange = (seriesBookings: Booking[]) => {
        const numbers = seriesBookings
            .map(b => b.bookingNumber)
            .sort((a, b) => a.localeCompare(b));
        if (numbers.length <= 1) return numbers[0] ?? '';
        // Find common prefix
        const first = numbers[0];
        const last = numbers[numbers.length - 1];
        let prefixLen = 0;
        while (prefixLen < first.length && first[prefixLen] === last[prefixLen]) {
            prefixLen++;
        }
        // Show e.g. "N-00000136–141"
        if (prefixLen > 0) {
            return `${first}–${last.slice(prefixLen)}`;
        }
        return `${first}–${last}`;
    };

    const renderSeriesRow = (entry: BookingTableEntry & { type: 'series' }) => {
        const { seriesId, bookings: seriesBookings } = entry;
        const isExpanded = expandedSeries.has(seriesId);
        const firstBooking = seriesBookings[0];
        const lastBooking = seriesBookings[seriesBookings.length - 1];

        const assetLabel = getLocationLabel(firstBooking);
        // Flag when at least one occurrence is booked at a different location
        // than the primary (first) one. Empty labels are ignored so missing
        // data doesn't produce a false positive.
        const hasLocationDeviation = seriesBookings.some((b) => {
            const loc = getLocationLabel(b);
            return loc !== '' && loc !== assetLabel;
        });

        return (
            <React.Fragment key={`series-${seriesId}`}>
                {/* Series parent row */}
                <TableRow
                    sx={{
                        backgroundColor: '#F3F4F6',
                        borderLeft: '3px solid #6B7280',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#E5E7EB' },
                    }}
                    onClick={onRowClick(() => toggleSeries(seriesId))}
                >
                    {/* No box on the series itself: its occurrences are the
                        bookings, selectable one by one once it is expanded. */}
                    {selectable && <TableCell padding={'checkbox'} />}
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size={'small'} sx={{ p: 0 }}>
                                {isExpanded
                                    ? <KeyboardArrowDownIcon fontSize={'small'} />
                                    : <KeyboardArrowRightIcon fontSize={'small'} />}
                            </IconButton>
                            <Box sx={{ fontSize: '12px', fontWeight: 600, color: '#1F2937' }}>
                                {getBookingNumberRange(seriesBookings)}
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell>
                        {useCaseLabels[firstBooking?.useCase as keyof typeof useCaseLabels]?.[currentLanguage as 'en' | 'de'] || firstBooking?.useCase}
                    </TableCell>
                    <TableCell>
                        {firstBooking?.startDate && (currentLanguage === 'de' || currentLanguage === 'en')
                            ? isRoomRental
                                ? formatDate(firstBooking.startDate, currentLanguage)
                                : formatDateTime(firstBooking.startDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                            : ''}
                    </TableCell>
                    <TableCell>
                        {lastBooking?.endDate && (currentLanguage === 'de' || currentLanguage === 'en')
                            ? isRoomRental
                                ? formatDate(lastBooking.endDate, currentLanguage)
                                : formatDateTime(lastBooking.endDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                            : ''}
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <span>{assetLabel || t('bookings.bookings_table.none')}</span>
                            {hasLocationDeviation && (
                                <Tooltip
                                    title={t('bookings.bookings_table.location_deviation_tooltip')}
                                    arrow
                                    placement={'top'}
                                >
                                    <Box
                                        component={'span'}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            color: 'text.secondary',
                                            borderRadius: '9999px',
                                            px: '8px',
                                            py: '1px',
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                            cursor: 'default',
                                        }}
                                    >
                                        {t('bookings.bookings_table.location_deviation_badge')}
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell>{firstBooking?.senderFullname}</TableCell>
                    {showLockerColumn && <TableCell />}
                    <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {getStatusSummary(seriesBookings).map(({ status, count }) => {
                                const colors = statusColors[status];
                                const label = t(
                                    `booking_details.status_badge.booking_status.${status}`,
                                );
                                return (
                                    <Box
                                        key={status}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                backgroundColor: colors?.bg ?? '#F3F4F6',
                                                color: colors?.text ?? '#374151',
                                                borderRadius: '9999px',
                                                px: '8px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    minWidth: 10,
                                                    minHeight: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: colors?.dot ?? '#9CA3AF',
                                                }}
                                            />
                                            {label}
                                        </Box>
                                        <Box sx={{ fontSize: '14px', fontWeight: 500, color: colors?.text ?? '#374151' }}>
                                            {count}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(isRoomRental ? getPaymentSummary(seriesBookings) : []).map(({ status, count }) => {
                                const colors = paymentColors[status];
                                // "Covered" is not a payment status but the badge
                                // the single-booking row shows, so it reads the same.
                                const label = status === COVERED_PAYMENT_STATUS
                                    ? t('bookings.bookings_table.covered_by_org')
                                    : t(`booking_details.status_badge.payment_status.${status}`);
                                return (
                                    <Box
                                        key={status}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                backgroundColor: colors?.bg ?? '#F3F4F6',
                                                color: colors?.text ?? '#374151',
                                                borderRadius: '9999px',
                                                px: '8px',
                                                fontSize: '14px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    minWidth: 10,
                                                    minHeight: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: colors?.dot ?? '#9CA3AF',
                                                }}
                                            />
                                            {label}
                                        </Box>
                                        <Box sx={{ fontSize: '14px', fontWeight: 500, color: colors?.text ?? '#374151' }}>
                                            {count}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Button
                            variant={'text'}
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewSeriesDetails(seriesId);
                            }}
                            sx={{
                                textDecoration: 'underline',
                                color: '#374151',
                                textTransform: 'none',
                                fontWeight: 500,
                                padding: 0,
                                minWidth: 'unset',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {t('bookings.bookings_table.action')}
                        </Button>
                    </TableCell>
                </TableRow>
                {/* Expanded child rows */}
                {/* Expanded occurrences open like single rows: a click on the
                    row or on the booking number. They carry no ⋮: archiving or
                    anonymising a single occurrence was never offered from this
                    list. */}
                {isExpanded && seriesBookings.map((booking, idx) => (
                    <TableRow
                        key={booking.bookingId}
                        hover={true}
                        onClick={onRowClick(() => onViewDetails(booking.bookingId, booking.bookingNumber))}
                        sx={{
                            cursor: 'pointer',
                            backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#F3F4F6',
                            borderLeft: '3px solid #D1D5DB',
                            '&:last-of-type td': { borderBottom: '2px solid #E5E7EB' },
                            ...(booking.archivedAt ? { opacity: 0.6 } : {}),
                        }}
                    >
                        {renderSelectCell(booking)}
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 28, flexShrink: 0 }} />
                                {renderBookingNumber(booking)}
                            </Box>
                        </TableCell>
                        <TableCell>
                            {useCaseLabels[booking?.useCase as keyof typeof useCaseLabels]?.[currentLanguage as 'en' | 'de'] || booking?.useCase}
                        </TableCell>
                        <TableCell>
                            {booking?.startDate && (currentLanguage === 'de' || currentLanguage === 'en')
                                ? isRoomRental
                                    ? formatDate(booking.startDate, currentLanguage)
                                    : formatDateTime(booking.startDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                                : ''}
                        </TableCell>
                        <TableCell>
                            {booking?.endDate && (currentLanguage === 'de' || currentLanguage === 'en')
                                ? isRoomRental
                                    ? formatDate(booking.endDate, currentLanguage)
                                    : formatDateTime(booking.endDate, currentLanguage, { includeTime: true, timeFormat: 'short' })
                                : ''}
                        </TableCell>
                        <TableCell>
                            {isRoomFamily(booking?.useCase)
                                ? booking?.langNames?.[i18n.language === 'de' ? 'de' : 'en']?.title ??
                                  booking?.assetName ??
                                  t('bookings.bookings_table.none')
                                : booking?.address ?? t('bookings.bookings_table.none')}
                        </TableCell>
                        <TableCell>{booking?.senderFullname}</TableCell>
                        {showLockerColumn && (
                            <TableCell>{booking?.door ?? t('bookings.bookings_table.none')}</TableCell>
                        )}
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <StatusBadge
                                    type={(booking?.bookingStatus.toLowerCase() as BookingStatusType) ?? 'booked'}
                                    isEditing={false}
                                />
                                {booking?.archivedAt && <StatusBadge type={'archived'} isEditing={false} />}
                                {renderApproval(booking)}
                                {renderCoverageUnderStatus(booking)}
                            </Box>
                        </TableCell>
                        <TableCell>
                            {renderPaymentCell(booking)}
                        </TableCell>
                        {/* The actions column stays, empty: nothing to change here. */}
                        <TableCell />
                    </TableRow>
                ))}
            </React.Fragment>
        );
    };

    const SortableHeader = ({ label, sortKeyName }: { label: string, sortKeyName: string }) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
            {label}
            {sortKey.key === sortKeyName && sortKey.order === 'asc' ? (
                <ArrowDownwardIcon
                    sx={{ fontSize: 12, color: '#9CA3AF', cursor: 'pointer' }}
                    onClick={() => setSortKey({ key: sortKeyName, order: 'desc' })}
                />
            ) : (
                <ArrowUpwardIcon
                    sx={{ fontSize: 12, color: '#9CA3AF', cursor: 'pointer' }}
                    onClick={() => setSortKey({ key: sortKeyName, order: 'asc' })}
                />
            )}
        </Box>
    );

    const headerCellSx = { fontSize: 12, color: '#9CA3AF' };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
                <Table
                    aria-label={'booking table'}
                    sx={{
                        '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root': {
                            borderBottom: 'none',
                            fontWeight: 500,
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0.02em',
                        },
                        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root': {
                            borderBottom: '1px solid #333333',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {selectable && (
                                <TableCell padding={'checkbox'}>
                                    <Checkbox
                                        checked={headerState === 'all'}
                                        indeterminate={headerState === 'some'}
                                        disabled={visibleSelectableIds.length === 0}
                                        onChange={() => onToggleAllSelected?.(visibleSelectableIds)}
                                        ariaLabel={t('bookings.bulk.select_all')}
                                    />
                                </TableCell>
                            )}
                            <TableCell sx={headerCellSx}>
                                <SortableHeader label={t('bookings.bookings_table.headers.booking_id')} sortKeyName={'bookingId'} />
                            </TableCell>
                            <TableCell sx={headerCellSx}>
                                {t('bookings.bookings_table.headers.service')}
                            </TableCell>
                            <TableCell sx={headerCellSx}>
                                <SortableHeader
                                    label={isRoomRental ? t('bookings.bookings_table.headers.check_in') : t('bookings.bookings_table.headers.start_time')}
                                    sortKeyName={'checkIn'}
                                />
                            </TableCell>
                            <TableCell sx={headerCellSx}>
                                <SortableHeader
                                    label={isRoomRental ? t('bookings.bookings_table.headers.check_out') : t('bookings.bookings_table.headers.end_time')}
                                    sortKeyName={'checkOut'}
                                />
                            </TableCell>
                            <TableCell sx={headerCellSx}>
                                {t('bookings.bookings_table.headers.address')}
                            </TableCell>
                            <TableCell sx={headerCellSx}>
                                {t('bookings.bookings_table.headers.customer_name')}
                            </TableCell>
                            {showLockerColumn && (
                                <TableCell sx={headerCellSx}>
                                    {t('bookings.bookings_table.headers.assigned_locker')}
                                </TableCell>
                            )}
                            <TableCell sx={headerCellSx}>
                                <SortableHeader label={t('bookings.bookings_table.headers.booking_status')} sortKeyName={'bookingStatus'} />
                            </TableCell>
                            {isRoomRental ? (
                                <TableCell sx={headerCellSx}>
                                    {t('bookings.bookings_table.headers.payment_status')}
                                </TableCell>
                            ) : (
                                <TableCell sx={headerCellSx}>
                                    <SortableHeader label={t('bookings.bookings_table.headers.issue')} sortKeyName={'issue'} />
                                </TableCell>
                            )}
                            <TableCell sx={headerCellSx}>
                                {t('bookings.bookings_table.headers.action')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columnCount + 1} align={'center'} sx={{ py: 6 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedEntries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columnCount + 1} align={'center'} sx={{ py: 6 }}>
                                    {t('bookings.bookings_table.no_bookings')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedEntries.map((entry) =>
                                entry.type === 'series'
                                    ? renderSeriesRow(entry)
                                    : renderBookingRow(entry.booking),
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[100, 200, 300, 500]}
                component={'div'}
                count={entries.length}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage={t('bookings.bookings_table.rows_per_page')}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                sx={{
                    color: '#333333',
                    '& .MuiTablePagination-selectLabel': {
                        fontSize: '12px',
                        marginRight: '-8px',
                        color: '#333333',
                    },
                    '& .MuiTablePagination-select': {
                        marginLeft: '10px',
                        marginBottom: '-3px',
                        marginRight: 0,
                        color: '#333333',
                    },
                    '& .MuiInputBase-root': {
                        marginLeft: 0,
                        marginRight: '8px',
                        color: '#333333',
                        fontSize: '12px',
                    },
                    '& .MuiTablePagination-actions': {
                        color: '#333333',
                    },
                    '& .MuiIconButton-root': {
                        color: '#333333',
                    },
                    '& .MuiTablePagination-displayedRows': {
                        color: '#333333',
                        fontSize: '12px',
                    },
                }}
            />
            <BookingArchiveDialog
                mode={actionMode}
                booking={actionBooking}
                onClose={closeAction}
                onDone={() => onRefetch?.()}
            />
        </Paper>
    );
};

export default BookingTable;
